import { computed, type ComputedRef, type Ref } from "vue";

import type { FieldConfig } from "../types";

/**
 * Condition for field visibility.
 */
interface ShowWhenCondition {
  field: string;
  operator: "=" | "!=" | "in" | "notIn";
  value?: unknown;
  values?: unknown[];
}

/**
 * Extract the raw data object from an Inertia form or return the object as-is.
 * Inertia's useForm has a .data() method that conflicts with properties named "data".
 * This helper safely extracts the actual form data and handles the case where
 * the form data object itself has a "data" property that is a function.
 */
export function getFormData(form: Record<string, unknown>): Record<string, unknown> {
  if (form && typeof (form as any).data === 'function') {
    const result = (form as any).data() as Record<string, unknown>;
    
    // Fix: If the result has a "data" property that is a function, we need to get
    // the actual nested data from the form's internal structure.
    // Inertia forms store the original data structure internally.
    if (result && typeof result.data === 'function') {
      // Access the actual nested data from the form's internal structure
      // Inertia forms store data in form._data or we can access it via Object.getOwnPropertyDescriptor
      const actualData: Record<string, unknown> = {};
      for (const key in result) {
        if (key !== 'data' && typeof result[key] !== 'function') {
          actualData[key] = result[key];
        }
      }
      
      // Try to get the nested data from various possible locations in Inertia's form structure
      const formAny = form as any;
      let nestedDataValue: unknown = undefined;
      
      // 1. Check form._initialData (we attach this in useCrudForm)
      if (formAny._initialData && formAny._initialData.data && typeof formAny._initialData.data !== 'function') {
        nestedDataValue = formAny._initialData.data;
      }
      // 2. Check form._originalData (Inertia's internal storage for original data)
      else if (formAny._originalData && formAny._originalData.data && typeof formAny._originalData.data !== 'function') {
        nestedDataValue = formAny._originalData.data;
      }
      // 3. Check form.defaults (some Inertia versions use this)
      else if (formAny.defaults && formAny.defaults.data && typeof formAny.defaults.data !== 'function') {
        nestedDataValue = formAny.defaults.data;
      }
      // 4. Check form._data (internal Inertia storage)
      else if (formAny._data && formAny._data.data && typeof formAny._data.data !== 'function') {
        nestedDataValue = formAny._data.data;
      }
      // 5. Check form.$data (Vue reactive data)
      else if (formAny.$data && formAny.$data.data && typeof formAny.$data.data !== 'function') {
        nestedDataValue = formAny.$data.data;
      }
      
      if (nestedDataValue !== undefined) {
        actualData.data = nestedDataValue;
      }
      
      return actualData;
    }
    return result;
  }
  return form;
}

/**
 * Convert camelCase to snake_case.
 * Example: 'employeeProfile' => 'employee_profile'
 */
function toSnakeCase(str: string): string {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

/**
 * Get a nested value from an object using dot notation.
 * Example: getNestedValue({ data: { date: '2025-01-01' } }, 'data.date') => '2025-01-01'
 *
 * Handles Vue/Inertia reactive proxies by using direct property access
 * instead of 'in' operator which may not work with all proxy implementations.
 *
 * Also handles camelCase to snake_case conversion for relation keys since
 * Laravel serializes relations as snake_case but field keys use camelCase.
 */
export function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  if (!obj || typeof obj !== "object") {
    return undefined;
  }

  if (!path.includes(".")) {
    // Try original key first, then snake_case version
    if (path in obj) {
      return obj[path];
    }
    const snakeKey = toSnakeCase(path);
    if (snakeKey !== path && snakeKey in obj) {
      return obj[snakeKey];
    }
    return obj[path];
  }

  const keys = path.split(".");
  let result: unknown = obj;

  for (const key of keys) {
    if (result === null || result === undefined) {
      return undefined;
    }

    if (typeof result !== "object") {
      return undefined;
    }

    const objResult = result as Record<string, unknown>;

    // Try original key first, then snake_case version
    if (key in objResult) {
      result = objResult[key];
    } else {
      const snakeKey = toSnakeCase(key);
      if (snakeKey !== key && snakeKey in objResult) {
        result = objResult[snakeKey];
      } else {
        result = objResult[key]; // Will be undefined
      }
    }
  }

  return result;
}

/**
 * Check whether a viewer item path resolves to an "empty" value
 * (null, undefined, empty string, or empty array).
 *
 * `CrudController::transformItemForViewer()` writes JSON-column dot-paths
 * (e.g. "app_data.items") as flat keys on the slim payload while loaded
 * relations come back as nested objects. This helper checks the flat key
 * first, then falls back to nested traversal via `getNestedValue` so the
 * `hideWhenEmpty` filter on tab configs works against either shape.
 */
export function isViewerPathEmpty(item: Record<string, unknown>, path: string): boolean {
  const flat = item[path];
  const value = flat !== undefined ? flat : getNestedValue(item, path);

  if (value === null || value === undefined) return true;
  if (typeof value === "string") return value.length === 0;
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

/**
 * Set a nested value in an object using dot notation.
 * Example: setNestedValue(obj, 'data.date', '2025-01-01') sets obj.data.date = '2025-01-01'
 * Creates intermediate objects if they don't exist.
 */
export function setNestedValue(obj: Record<string, unknown>, path: string, value: unknown): void {
  if (!path.includes(".")) {
    obj[path] = value;
    return;
  }

  const keys = path.split(".");
  const lastKey = keys.pop()!;

  let current: Record<string, unknown> = obj;
  for (const key of keys) {
    if (!(key in current) || typeof current[key] !== "object" || current[key] === null) {
      current[key] = {};
    }
    current = current[key] as Record<string, unknown>;
  }

  current[lastKey] = value;
}

/**
 * Composable to determine field visibility based on showWhen conditions.
 *
 * @example
 * const { isFieldVisible, visibleFields } = useFieldVisibility(
 *   computed(() => block.fields),
 *   computed(() => form)
 * );
 */
export function useFieldVisibility(
  fields: Ref<FieldConfig[]> | ComputedRef<FieldConfig[]>,
  formData: Ref<Record<string, unknown>> | ComputedRef<Record<string, unknown>>
) {

  /**
   * Check if a single condition is satisfied.
   */
  function evaluateCondition(
    condition: ShowWhenCondition,
    data: Record<string, unknown>
  ): boolean {
    const fieldValue = getNestedValue(data, condition.field);

    switch (condition.operator) {
      case "=":
        return fieldValue === condition.value;
      case "!=":
        return fieldValue !== condition.value;
      case "in":
        return condition.values?.includes(fieldValue) ?? false;
      case "notIn":
        return !(condition.values?.includes(fieldValue) ?? false);
      default:
        return true;
    }
  }

  /**
   * Check if a field should be visible based on its showWhen condition.
   */
  function isFieldVisible(field: FieldConfig): boolean {
    const condition = field.showWhen as ShowWhenCondition | null | undefined;
    if (!condition) return true;
    // Use getFormData to handle Inertia form's .data() method conflict
    const data = getFormData(formData.value as Record<string, unknown>);
    return evaluateCondition(condition, data);
  }

  /**
   * Get only visible fields (reactive).
   */
  const visibleFields = computed(() =>
    fields.value.filter((field) => isFieldVisible(field))
  );

  return {
    isFieldVisible,
    visibleFields
  };
}
